import { useEffect, useState } from 'react';
import type { MealSlot } from '@kraftwerk/shared';
import { db } from '../../db/local.js';
import { loadRecipeImage, recipeImageUrl } from '../../db/recipeImages.js';
import { IconNutrition } from '../Icons.js';

/**
 * Geöffnete Bilder je Fassung — eine Liste mit vielen Gerichten soll nicht bei
 * jedem Wechsel neu holen und entschlüsseln (E21). Gedeckelt, die ältesten
 * gehen zuerst.
 */
const opened = new Map<string, string>();
const MAX_OPENED = 80;

function remember(url: string, objectUrl: string): void {
  opened.set(url, objectUrl);
  for (const [key, value] of opened) {
    if (opened.size <= MAX_OPENED) break;
    URL.revokeObjectURL(value);
    opened.delete(key);
  }
}

/**
 * Titelbild eines Gerichts — oder ein ruhiger Platzhalter.
 *
 * Fehlt das Bild (noch keins, offline, gelöscht, nicht zu öffnen), steht
 * dieselbe Fläche da: Eine Kachel, die beim Laden springt, verschiebt den
 * Finger auf die falsche. Das Bild ist Schmuck, der Name steht daneben —
 * deshalb `alt=""`. Verschlüsselte Bilder (E21) öffnet das Gerät selbst.
 */
export function RecipeImage({
  recipe,
  className = '',
  lazy = false,
}: {
  recipe: { id: string; imageAt?: string | null; slot: MealSlot };
  className?: string;
  /** Nur in langen Listen — ein einzelnes Bild oben auf der Seite lädt sofort. */
  lazy?: boolean;
}): React.JSX.Element {
  const url = recipeImageUrl(recipe);
  const [shown, setShown] = useState<{ url: string; src: string | null } | null>(null);

  useEffect(() => {
    if (!url) return;
    const hit = opened.get(url);
    if (hit) {
      setShown({ url, src: hit });
      return;
    }
    let live = true;
    void loadRecipeImage({ id: recipe.id, imageAt: recipe.imageAt ?? null }, db).then((blob) => {
      if (!live) return;
      if (!(blob instanceof Blob)) {
        setShown({ url, src: null });
        return;
      }
      const objectUrl = URL.createObjectURL(blob);
      remember(url, objectUrl);
      setShown({ url, src: objectUrl });
    });
    return () => {
      live = false;
    };
  }, [url, recipe.id, recipe.imageAt]);

  const src = url && shown?.url === url ? shown.src : url ? (opened.get(url) ?? null) : null;

  if (!src) {
    return (
      <div
        aria-hidden="true"
        className={`flex items-center justify-center bg-surface-raised text-ink-muted ${className}`}
      >
        <IconNutrition />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt=""
      loading={lazy ? 'lazy' : 'eager'}
      decoding="async"
      className={`object-cover ${className}`}
    />
  );
}
